import {
  type ElementType,
  type ReactNode,
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";

import { Typography } from "@mui/material";
import { LoaderContext, GlobalContext } from "@/helpers/contexts/contexts";
import { reportService } from "@/shared/services/reportService";
import NoDataFound from "@/components/NoDataFound";
import { renderTemplate } from "@/shared/helpers/reportHelper";
import { LocationList } from "@/features/locations/location-list";
import { BrandDigitalCampaignList as BrandDigitalCampaignListBase } from "@/features/campaigns/digital/brand-digital-campaign-list";
import DoughnutChartBase from "@/components/charts/DoughnutChart";
import RPCustomDateRagePicker from "@/components/RPCustomDateRagePicker";
import TotalSpendChartBase from "@/components/charts/TotalSpendChart";
import BarChartBase from "@/components/charts/BarChart";
import {
  getDashboardPath,
  getTotalSpendSum,
  removeCampaignByNameLike,
} from "@/helpers/utils";
import PageLayout from "@/components/layouts/page/PageLayout";
import PageHeaderBase from "@/components/layouts/pageHeaders/PageHeader";
import UniversalWidgetTemplateBase from "@/components/templates/UniversalWidgetTemplate";
import { MUICustomAutocompleteSelect } from "@/components/muiCustom/select/MUICustomAutocompleteSelect";
import { BrandDirectMailCampaignList as BrandDirectMailCampaignListBase } from "@/features/campaigns/direct-mail/brand-direct-mail-campaign-list";
import useFetcher from "@/helpers/hooks/useFetcher";
import { CUSTOM_WIDGET_PARAM } from "@/config/constants";
import StackedBarChartBase from "@/components/charts/StackedBarChart";
import { getRedirectPath } from "@/helpers/localStore";

type DashboardRecord = Record<string, any>;
type DataFilters = Record<
  string,
  {
    filter?: string;
    from?: string | Date | null;
    to?: string | Date | null;
  }
>;
type GlobalContextValue = {
  loggedInUser?: DashboardRecord;
  selectedBrand?: DashboardRecord;
};
type Loader = (_show: boolean) => void;

const DoughnutChart = DoughnutChartBase as ElementType;
const BarChart = BarChartBase as ElementType;
const StackedBarChart = StackedBarChartBase as ElementType;
const TotalSpendChart = TotalSpendChartBase as ElementType;
const PageHeader = PageHeaderBase as ElementType;
const UniversalWidgetTemplate = UniversalWidgetTemplateBase as ElementType;
const BrandDigitalCampaignList = BrandDigitalCampaignListBase as ElementType;
const BrandDirectMailCampaignList =
  BrandDirectMailCampaignListBase as ElementType;

const BrandDashboardPage = () => {
  const loader = useContext(LoaderContext) as Loader;
  const { selectedBrand, loggedInUser } = useContext(
    GlobalContext
  ) as GlobalContextValue;
  const navigate = useNavigate();
  const location = useLocation();

  const [dataFilters, setDataFilters] = useState<DataFilters>({});
  const currentPath = location.pathname;

  const reqData = useMemo(() => {
    const reqData: DashboardRecord = {
      id: selectedBrand?.id,
      userId: loggedInUser?.id,
      dataType: "brand",
      system_generated_brand_id: selectedBrand?.system_generated_brand_id,
    };

    if (dataFilters && !isEmpty(dataFilters)) {
      const filter_widget_id: string[] = [];
      Object.keys(dataFilters).forEach((key) => {
        if (dataFilters[key].from && dataFilters[key].to) {
          filter_widget_id.push(key);
          reqData[`fromdate_${key}`] = moment(dataFilters[key].from).format(
            "MM-DD-YYYY"
          );
          reqData[`todate_${key}`] = moment(dataFilters[key].to).format(
            "MM-DD-YYYY"
          );
        } else if (dataFilters[key].filter) {
          filter_widget_id.push(key);
          reqData[key] = dataFilters[key].filter
            .toLowerCase()
            .replace(/\s/g, "_");
        }
      });
      reqData["filter_widget_id"] = filter_widget_id;
    }

    return reqData;
  }, [
    selectedBrand?.id,
    selectedBrand?.system_generated_brand_id,
    loggedInUser?.id,
    dataFilters,
  ]);

  const { data: report, isLoading: loading } = useFetcher(
    selectedBrand?.id && reqData,
    reportService.getDashboardReport,
    { revalidateOnFocus: false }
  );

  const handleOnChangeDataFilter = (id: string | number, filter: string) => {
    setDataFilters({ ...dataFilters, [id]: { filter } });
  };

  const handleDateRange = (
    id: string | number,
    range?: [string | Date | null, string | Date | null]
  ) => {
    if (range && range[0] && range[1]) {
      setDataFilters({
        ...dataFilters,
        [id]: { ...dataFilters[id], from: range[0], to: range[1] },
      });
    }
  };

  useEffect(() => {
    if (!selectedBrand?.id) return;

    const storedRedirectPath = getRedirectPath();

    if (storedRedirectPath) {
      if (currentPath !== storedRedirectPath) {
        navigate(storedRedirectPath, { replace: true });
      }
      return;
    }

    const validDashboardPaths = ["/dashboard", "/overview-dashboard"];

    if (!validDashboardPaths.includes(currentPath)) {
      const defaultPath = getDashboardPath();
      navigate(`${defaultPath}`, { replace: true });
    }
    // Preserve the existing redirect dependency behavior during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand, navigate, loggedInUser]);

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  useEffect(() => {
    return () => loader(false);
    // Preserve the existing unmount cleanup dependency behavior during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <PageLayout>
        <NoDataFound message="Loading..." />
      </PageLayout>
    );
  }

  if (!report || !report.template.rows || report.template.rows.length === 0) {
    const NoDataFoundMessage =
      location.pathname === "/overview-dashboard"
        ? "There is no template defined for this parent company"
        : "There is no template defined for this brand";
    return (
      <PageLayout>
        <NoDataFound message={NoDataFoundMessage} />
      </PageLayout>
    );
  }

  const renderChartWidget = (
    widget: DashboardRecord,
    template?: DashboardRecord
  ): ReactNode => {
    let filterToRender: ReactNode = null;
    let chartToRender: ReactNode = null;

    const filters = template?.filters ? template.filters?.split(",") : [];
    if (template?.apply_filter && filters && filters.length) {
      const formattedFilters = filters.map((f: string) => ({
        label: f,
        value: f,
      }));

      if (widget.widget_for === "brand" && widget.id === 7) {
        formattedFilters.unshift({
          label: "Last 3 Months",
          value: "Last 3 Months",
        });
      }

      filterToRender = (
        <div className="row text16P_Bold548E8D justify-content-end align-items-center">
          {widget.chart_type === "total_spend" && widget?.data?.length ? (
            <div className="col mb-1">
              <Typography color="text.primary" variant="body2" className="fw-b">
                Total: {getTotalSpendSum(widget)}
              </Typography>
            </div>
          ) : null}
          <div className="col mb-1">
            <div className="form-group d-flex align-items-center mb-0 justify-content-end">
              <MUICustomAutocompleteSelect
                options={formattedFilters.map((f) => ({
                  label: f.label,
                  value: f.value,
                }))}
                selectProps={{
                  onChange: (v: string) =>
                    handleOnChangeDataFilter(widget.id, v),
                  value: dataFilters[widget.id]
                    ? dataFilters[widget.id].filter
                    : "Last 3 Months",
                }}
                wrapperProps={{ style: { width: 150 } }}
                name="total_spend_filter"
                size="small"
                hideLabel
                className="mt-0"
              />
            </div>
          </div>

          {dataFilters[widget.id] &&
          dataFilters[widget.id].filter === "Custom Date Range" ? (
            <div className="col-auto mb-1 d-flex justify-content-end custom-date-range-picker">
              <RPCustomDateRagePicker
                onChangeDate={(
                  d: [string | Date | null, string | Date | null]
                ) => handleDateRange(widget.id, d)}
                range={
                  dataFilters[widget.id]?.from && dataFilters[widget.id]?.to
                    ? [dataFilters[widget.id].from, dataFilters[widget.id].to]
                    : [null, null]
                }
              />
            </div>
          ) : null}
        </div>
      );

      if (widget.id === 8) {
        filterToRender = null;
      }
    }

    if (!widget.data || widget.data.length === 0) {
      return (
        <>
          {filterToRender}
          <NoDataFound message="There is no record" height="100%" />
        </>
      );
    }

    if (!Array.isArray(widget.data)) {
      return (
        <>
          {filterToRender}
          <NoDataFound message="There is not in Array format" height="100%" />
        </>
      );
    }

    const labels = Object.keys(widget.data[0]);

    if (!labels || labels.length === 0) {
      return (
        <>
          {filterToRender}
          <NoDataFound message="There is no record" height="100%" />
        </>
      );
    }

    if (widget.chart_type === "pie" || widget.chart_type === "doughnut") {
      chartToRender = (
        <DoughnutChart
          data={widget.data}
          colorSet={1}
          labels={labels}
          labelHeading="Campaign Name"
          valueHeading="Participant Rate"
          formats={widget?.format}
        />
      );
    } else if (widget.chart_type === "bar") {
      chartToRender = (
        <BarChart
          data={widget.data}
          labels={labels}
          title={widget.alt_name || widget.name}
          formats={widget?.format}
        />
      );
    } else if (widget.chart_type === "stacked_bar") {
      chartToRender = (
        <StackedBarChart
          data={widget.data}
          title={widget.alt_name || widget.name}
          formats={widget?.format}
        />
      );
    } else if (widget.chart_type === "total_spend") {
      chartToRender = (
        <TotalSpendChart
          data={widget.data}
          labels={labels}
          title={widget.alt_name || widget.name}
          formats={widget?.format}
        />
      );
    }

    return (
      <>
        <div>{filterToRender}</div>
        {chartToRender}
      </>
    );
  };

  const renderTable = (widget: DashboardRecord): ReactNode => {
    if (!widget.data || widget.data.length === 0) {
      return <NoDataFound message="There is no record" height="100%" />;
    }

    if (widget.id === 11) {
      return (
        <LocationList
          selectedBrand={selectedBrand}
          loggedInUser={loggedInUser}
        />
      );
    } else if (widget.id === 12) {
      const noBoostCamp = removeCampaignByNameLike(widget.data, [
        "boost",
        "boost:",
      ]);
      return (
        <BrandDigitalCampaignList
          campaigns={noBoostCamp}
          fromPage="dashboard"
        />
      );
    } else if (widget.id === 195) {
      const noBoostCamp = removeCampaignByNameLike(widget.data, [
        "boost",
        "boost:",
      ]);
      return <BrandDirectMailCampaignList campaigns={noBoostCamp} />;
    }

    return null;
  };

  return (
    <PageLayout className="admin-dashboard-container mx-0 mx-sm-2">
      <PageHeader showName showLastUpdate showButton />
      <div className="pb-5 dashboard admin report">
        <UniversalWidgetTemplate
          rows={report?.template?.universal_widget_rows}
          report={report}
          renderChartWidget={renderChartWidget}
          type="brand"
          embedWidgetParams={{
            [CUSTOM_WIDGET_PARAM.BrandID]:
              selectedBrand?.system_generated_brand_id,
          }}
          hideDateSelector
        />
        {renderTemplate({
          report,
          renderTable,
          renderChartWidget,
          embedWidgetParams: {
            [CUSTOM_WIDGET_PARAM.BrandID]:
              selectedBrand?.system_generated_brand_id,
          },
        })}
      </div>
    </PageLayout>
  );
};

export default BrandDashboardPage;
